import { all } from '../lib/dom';

/**
 * Form field decoration.
 *
 * The stylesheet keys its floating-label and field-type styling off classes on the
 * `.gfield` wrapper, which the form markup does not carry on its own. This adds
 * them, then keeps `has_value` in sync so a filled field keeps its label raised.
 *
 * Only `/contact/` has a form, so everything here no-ops elsewhere.
 */

/** A phone input's mask shows this while empty; it must not count as a value. */
const EMPTY_TEL_MASK = '(___) ___-____';

type FieldInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const IGNORED_INPUT_TYPES = new Set(['radio', 'checkbox', 'file', 'hidden']);

export function forms(): void {
  const fields = all<HTMLElement>('.gfield');
  if (fields.length === 0) return;

  for (const el of all('.gfield_select')) {
    el.closest('.gfield')?.classList.add('has-select');
  }

  for (const el of all('.ginput_container_textarea')) {
    el.closest('.gfield')?.classList.add('has-textarea');
  }

  for (const input of all<HTMLInputElement>('input')) {
    if (IGNORED_INPUT_TYPES.has(input.type)) continue;
    input.closest('.gfield')?.classList.add('has-text-input');
  }

  const inputs = all<FieldInput>('input, textarea, select');

  for (const input of inputs) {
    syncValueState(input);
    // `input` as well as `change`/`blur`, so the label reacts as you type rather
    // than only once the field is left.
    input.addEventListener('input', () => syncValueState(input));
    input.addEventListener('change', () => syncValueState(input));
    input.addEventListener('blur', () => syncValueState(input));
  }
}

function syncValueState(input: FieldInput): void {
  const field = input.closest('.gfield');
  if (!field) return;

  const value = input.value;
  const filled = value !== '' && value !== EMPTY_TEL_MASK;
  field.classList.toggle('has_value', filled);
}
